import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@lib/supabase';
import { useAuth } from '@lib/useAuth';
import { useUserRole } from '@lib/useUserRole';
import { showErrorToast, showSuccessToast } from '@lib/toast';

const FEEDBACK_CATEGORIES = [
  { label: 'Idea', value: 'idea' },
  { label: 'Improvement', value: 'improvement' },
  { label: 'Bug', value: 'bug' },
];

export default function FeedbackWidget() {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const defaultCategory = FEEDBACK_CATEGORIES[0]?.value ?? 'idea';
  const [category, setCategory] = useState(defaultCategory);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSendDisabled = submitting || message.trim().length === 0;

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      setError('Please describe your idea, improvement, or bug.');
      return;
    }

    setSubmitting(true);
    try {
      // insert and return the created row so we can reference its id
      const { data, error: insertError } = await supabase
        .from('feedback')
        .insert({
          user_id: user?.id ?? null,
          category,
          message: trimmedMessage,
          page: location.pathname,
        })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      showSuccessToast('Feedback sent. Thank you!');
      setMessage('');
      setCategory(defaultCategory);
      setOpen(false);

      // attempt to notify support in the background; failures shouldn't affect UX
      (async () => {
        try {
          await fetch('/api/feedback-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              feedbackId: data?.id,
              userId: user?.id,
            }),
          });
        } catch (err) {
          console.error('Failed to send feedback email to support', err);
        }
      })();
    } catch (err: unknown) {
      console.error('Failed to submit feedback', err);
      const friendlyMessage =
        err instanceof Error
          ? err.message
          : 'Something went wrong saving your feedback. Please try again.';
      setError(friendlyMessage);
      showErrorToast(friendlyMessage);
    } finally {
      setSubmitting(false);
    }
  }, [category, location.pathname, message, user]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const activeCategoryClasses = useMemo(
    () => 'item-selected text-white shadow-sm shadow-primary-500/40',
    []
  );

  const categoryButtonClasses = (value: string) => {
    return [
      'rounded-full px-4 py-1.5 text-sm font-semibold transition',
      category === value
        ? activeCategoryClasses
        : 'border border-gray-200 bg-white text-slate-700 hover:border-slate-400',
    ].join(' ');
  };

  // Don't show feedback widget on breathing or Buddha game pages or to admins
  const { role } = useUserRole(user?.id);
  const shouldShowFeedback =
    Boolean(user) &&
    role !== 'admin' &&
    !location.pathname.startsWith('/breathing') &&
    !location.pathname.startsWith('/buddha-game');

  if (!shouldShowFeedback) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div
            role="dialog"
            aria-modal="true"
            className="relative w-full max-w-sm rounded-3xl bg-white/95 p-6 shadow-2xl shadow-slate-900/40 backdrop-blur"
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Your feedback
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Thanks for taking a moment to share your thoughts. Your
                  feedback is truly appreciated.
                </p>
              </div>
              <button
                type="button"
                className="rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                onClick={() => setOpen(false)}
                aria-label="Close feedback form"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-slate-600">Category</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {FEEDBACK_CATEGORIES.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setCategory(item.value)}
                      className={categoryButtonClasses(item.value)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600">
                  Message
                </label>
                <textarea
                  className="mt-2 h-28 w-full resize-none rounded-2xl border border-gray-200 bg-slate-50 p-3 text-sm leading-relaxed text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  placeholder="Tell us about your idea, what we could improve, or the bug you spotted..."
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                />
              </div>

              {error && (
                <p className="text-xs font-semibold text-rose-600">{error}</p>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="begin-breathing-button w-full rounded-2xl px-5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={handleSubmit}
                  disabled={isSendDisabled}
                >
                  {submitting ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-2 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 px-5 py-3 text-sm font-semibold text-white shadow-2xl shadow-slate-900/40 transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        aria-expanded={open}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4 text-white"
            aria-hidden="true"
          >
            <path d="M2 12a10 10 0 0110-10 10 10 0 0110 10c0 5.5-4.5 10-10 10a10 10 0 01-7.5-3.1L2 22z" />
            <path d="M8 12h8" />
            <path d="M8 16h4" />
          </svg>
        </span>
        <span className="text-white">Give us your Feedback</span>
      </button>
    </div>
  );
}
